/*
 * PDP › box engine: the delivery / returns / Try Before You Buy rows and their sheets.
 *
 * Dates are computed in the browser (Liquid `now` is cache-stale) against the shop's clock
 * (Asia/Bangkok), so the cut-off logic is right whatever the visitor's timezone. Free shipping
 * follows the cart total; returns, Try Before You Buy and pickup follow the selected variant.
 * Pickup locations come from the native pickup widget, which stays in the DOM (its own preview
 * and drawer are hidden) and is re-fetched per variant by product-info.js.
 *
 * The pure helpers are exported for tests; the custom element below calls the same code.
 */
(function (global) {
  function isShippingDay(date, shipWeekends) {
    if (shipWeekends) return true;
    const dow = date.getUTCDay(); // 0 = Sun, 6 = Sat
    return dow !== 0 && dow !== 6;
  }

  function nextShippingDay(date, shipWeekends) {
    const next = new Date(date);
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (!isShippingDay(next, shipWeekends));
    return next;
  }

  function addShippingDays(date, count, shipWeekends) {
    let result = new Date(date);
    for (let i = 0; i < count; i += 1)
      result = nextShippingDay(result, shipWeekends);
    return result;
  }

  // Couriers deliver 7 days a week, so transit is counted in calendar days, not business days.
  function addCalendarDays(date, count) {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + count);
    return result;
  }

  // now = { day: Date (UTC midnight of the shop-local date), hour: shop-local hour 0-23 }
  function computeDeliveryRange(now, opts) {
    const shipWeekends = !!opts.shipWeekends;
    // Order is "received" today only if the warehouse ships today and we're before the cut-off;
    // otherwise it starts the next shipping day.
    let received = now.day;
    if (now.hour >= opts.cutoff || !isShippingDay(received, shipWeekends)) {
      received = nextShippingDay(received, shipWeekends);
    }
    // Dispatch advances by configured shipping days; the delivery window then uses calendar days.
    const dispatch = addShippingDays(received, opts.processing, shipWeekends);
    return {
      earliest: addCalendarDays(dispatch, opts.transitMin),
      latest: addCalendarDays(dispatch, opts.transitMax),
    };
  }

  // International parcels: dispatch after `handling` working days, then transit counted in
  // working days (Monday–Friday) — carriers quote export lanes that way, unlike domestic couriers.
  function computeLaneRange(now, opts) {
    const shipWeekends = !!opts.shipWeekends;
    let received = now.day;
    if (now.hour >= opts.cutoff || !isShippingDay(received, shipWeekends)) {
      received = nextShippingDay(received, shipWeekends);
    }
    const dispatch = addShippingDays(received, opts.handling, shipWeekends);
    return {
      earliest: addShippingDays(dispatch, opts.transitMin, false),
      latest: addShippingDays(dispatch, opts.transitMax, false),
    };
  }

  // "22–24 Oct", "30 Oct – 6 Nov": a range of days without weekdays, for international estimates.
  function formatDayRange(earliest, latest, locale) {
    const parts = (date) => {
      const all = new Intl.DateTimeFormat(locale || "en", {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
        calendar: "gregory",
      }).formatToParts(date);
      const part = (type) => (all.find((p) => p.type === type) || {}).value;
      return { day: part("day"), month: part("month") };
    };
    const last = parts(latest);
    if (earliest.getTime() === latest.getTime()) return `${last.day} ${last.month}`;
    const first = parts(earliest);
    return first.month === last.month
      ? `${first.day}–${last.day} ${last.month}`
      : `${first.day} ${first.month} – ${last.day} ${last.month}`;
  }

  // Same-day express in Phuket: "today" before the cut-off, "tomorrow" once it has passed.
  function phuketLabel(hour, cutoff, todayText, tomorrowText) {
    return hour < cutoff ? todayText : tomorrowText;
  }

  function dateParts(date, locale, style) {
    // calendar:'gregory' stops th-TH from printing the Buddhist-era calendar.
    const parts = new Intl.DateTimeFormat(locale || "en", {
      timeZone: "UTC",
      weekday: style,
      day: "numeric",
      month: style,
      calendar: "gregory",
    }).formatToParts(date);
    const part = (type) => (parts.find((p) => p.type === type) || {}).value;
    return { weekday: part("weekday"), day: part("day"), month: part("month") };
  }

  // "Thu 9 – Fri 10 Oct" (style "short", the row) or "Thursday 9 – Friday 10 October" (style
  // "long", the sheet). The month is printed once when both ends share it. Composed from parts
  // rather than Intl's formatRange, whose shape differs by engine and locale.
  function formatDateRange(earliest, latest, opts) {
    const style = opts.style || "short";
    const last = dateParts(latest, opts.locale, style);
    const lastText = `${last.weekday} ${last.day} ${last.month}`;
    if (opts.range === false || earliest.getTime() === latest.getTime())
      return lastText;
    const first = dateParts(earliest, opts.locale, style);
    const firstText =
      first.month === last.month
        ? `${first.weekday} ${first.day}`
        : `${first.weekday} ${first.day} ${first.month}`;
    return `${firstText} – ${lastText}`;
  }

  // Current shop-local date (UTC midnight) + hour, independent of the visitor's timezone.
  function nowInTimeZone(timeZone, clock) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      hour12: false,
    }).formatToParts(clock || new Date());
    const part = (type) =>
      parseInt(parts.find((p) => p.type === type).value, 10);
    let hour = part("hour");
    if (hour === 24) hour = 0; // some engines emit 24 for midnight
    return {
      day: new Date(Date.UTC(part("year"), part("month") - 1, part("day"))),
      hour,
    };
  }

  // Shopify's money format (shop.money_format, e.g. "฿{{amount_no_decimals}}") applied to an
  // amount in subunits, with trailing zero decimals dropped like money_without_trailing_zeros.
  function formatMoney(cents, format) {
    const group = (value, decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split(".");
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction && !/^0+$/.test(fraction)
        ? grouped + decimal + fraction
        : grouped;
    };
    const styles = {
      amount: [2, ",", "."],
      amount_no_decimals: [0, ",", "."],
      amount_with_comma_separator: [2, ".", ","],
      amount_no_decimals_with_comma_separator: [0, ".", ","],
      amount_with_apostrophe_separator: [2, "'", "."],
      amount_no_decimals_with_space_separator: [0, " ", ","],
      amount_with_space_separator: [2, " ", ","],
      amount_with_period_and_space_separator: [2, " ", "."],
    };
    return String(format || "{{amount}}").replace(
      /\{\{\s*(\w+)\s*\}\}/,
      (match, name) => {
        const [decimals, thousands, decimal] = styles[name] || styles.amount;
        return group(cents, decimals, thousands, decimal);
      },
    );
  }

  // The pickup widget prints Shopify's own pick_up_time ("Usually ready in 2 hours", translated
  // by Shopify). The sheets say "Ready in 2 hours"; anything without an hour count is shown as
  // Shopify wrote it.
  function readyLine(raw, templates) {
    const text = String(raw || "").trim();
    const hours = text.match(/(\d+)\s*(hours?|ชั่วโมง)/i);
    if (!hours) return text.charAt(0).toUpperCase() + text.slice(1);
    const n = parseInt(hours[1], 10);
    const template = n === 1 ? templates.one : templates.many;
    return String(template || "").replace("{n}", n);
  }

  function mapsUrl(query) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      query,
    )}`;
  }

  // Lines of a format_address render, without the country line, joined for display.
  function addressFromLines(lines, country) {
    const clean = lines
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    if (country && clean[clean.length - 1] === country) clean.pop();
    return { short: clean.slice(0, 2).join(", "), query: clean.join(", ") };
  }

  // The widget's drawer lists every pickup-enabled location for the variant it fetched; only
  // the ones with a tick are stocked. Address text is reused from the Liquid render when the
  // location is already known, so the line reads the same before and after a variant change.
  function locationsFromDrawer(drawer, known) {
    if (!drawer) return [];
    return Array.from(
      drawer.querySelectorAll(".pickup-availability-list__item"),
    )
      .filter((item) => item.querySelector(".pickup-availability-preview svg"))
      .map((item) => {
        const name = (item.querySelector("h3")?.textContent || "").trim();
        const status = (
          item.querySelector(".pickup-availability-preview")?.textContent || ""
        ).trim();
        // format_address prints one <p> with <br> between lines; a phone, when the
        // location has one, follows in a second <p>.
        const address = item.querySelector("address");
        const formatted = address && (address.querySelector("p") || address);
        const lines = [""];
        if (formatted) {
          formatted.childNodes.forEach((node) => {
            if (node.nodeName === "BR") lines.push("");
            else if (node.nodeName !== "P")
              lines[lines.length - 1] += node.textContent;
          });
        }
        const parsed = addressFromLines(lines);
        const knownLocation = known && known[name];
        return {
          name,
          // "Pickup available, usually ready in 2 hours" → the part after the comma.
          ready: status.includes(",")
            ? status.slice(status.indexOf(",") + 1).trim()
            : status,
          address: knownLocation ? knownLocation.address : parsed.short,
          query: knownLocation ? knownLocation.query : parsed.query,
        };
      })
      .filter((location) => location.name);
  }

  // How long a closed picker may still deliver its add (the request can outlive the close).
  const PICKER_CLOSE_GRACE = 3000;
  // The picker's mobile sheet and its desktop dropdown both announce a close from the picker.
  const PICKER_CLOSE_EVENTS = ["pdp-sheet:close", "pdp-size-picker:close"];

  function setOpen(sheet, open, opener) {
    if (!sheet) return;
    const method = open ? "open" : "close";
    if (typeof sheet[method] === "function") sheet[method](opener);
    else if (open) sheet.setAttribute("open", "");
    else sheet.removeAttribute("open");
  }

  // The international estimate element (delivery-estimate.js) reuses the same clock and rules.
  // Published before anything else runs, with an event for an element that was there first.
  global.UmiDeliveryDates = { nowInTimeZone, computeLaneRange, formatDayRange };
  if (global.document && typeof global.CustomEvent === "function")
    global.document.dispatchEvent(new global.CustomEvent("umi:delivery-dates"));

  if (
    typeof HTMLElement !== "undefined" &&
    global.customElements &&
    !customElements.get("pdp-trust-badges")
  ) {
    class PdpTrustBadges extends HTMLElement {
      connectedCallback() {
        // The sheets are fixed-position; an ancestor with a transform (the column's
        // reveal-on-scroll animation) would trap them, so they live on <body>.
        // Kept by reference so a box moved in the DOM finds its sheets again.
        const found = Array.from(this.querySelectorAll("pdp-sheet"));
        if (found.length) this.ownSheets = found;
        this.sheets().forEach((sheet) => document.body.append(sheet));
        this.finalSaleIds = new Set(
          (this.dataset.finalSaleIds || "").split(",").filter(Boolean),
        );
        this.tbybIds = new Set(
          (this.dataset.tbybIds || "").split(",").filter(Boolean),
        );
        this.tbybEligible = this.dataset.tbybEligible === "true";
        this.pickupAvailable = this.dataset.pickupAvailable === "true";
        this.locations = this.readInitialLocations();
        this.knownLocations = Object.fromEntries(
          this.locations.map((location) => [location.name, location]),
        );

        this.renderDate();
        this.renderPhuket();
        this.renderLocations();
        this.bindClicks();
        this.observePickup();
        this.refreshFreeShipping();
        if (typeof subscribe === "function") {
          // Free shipping tracks the cart total, so refresh on every cart change.
          this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
            this.refreshFreeShipping(),
          );
          // Returns, Try Before You Buy and pickup all depend on the selected variant.
          this.variantUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              const variant = event && event.data && event.data.variant;
              if (variant) this.updateVariantState(variant.id);
            },
          );
        }
      }

      disconnectedCallback() {
        if (this.cartUnsubscriber) this.cartUnsubscriber();
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.stopTryBeforeYouBuyIntent) this.stopTryBeforeYouBuyIntent();
        if (this.pickupObserver) this.pickupObserver.disconnect();
        if (this.onDocumentClick)
          document.removeEventListener("click", this.onDocumentClick);
        // A product swap re-renders the section; its new box brings its own sheets.
        if (this.ownSheets) this.ownSheets.forEach((sheet) => sheet.remove());
      }

      // This box's own sheets, held by reference: during a product swap the old and the new
      // section briefly share ids, so a lookup by id could reach the old box's sheets.
      sheet(name) {
        const id = `PdpSheet-${name}-${this.dataset.sectionId}`;
        return this.sheets().find((sheet) => sheet.id === id) || null;
      }

      sheets() {
        return this.ownSheets || [];
      }

      // Every element the engine renders into: the box itself and its sheets, which the sheet
      // element may have moved elsewhere in the document.
      targets(selector) {
        return [this, ...this.sheets()].flatMap((root) =>
          Array.from(root.querySelectorAll(selector)),
        );
      }

      readInitialLocations() {
        const data = this.querySelector("[data-tb-locations-initial]");
        try {
          return data ? JSON.parse(data.textContent) : [];
        } catch (e) {
          return [];
        }
      }

      // Delegated, so it reaches openers inside sheets wherever the sheet element lives.
      bindClicks() {
        this.onDocumentClick = (event) => {
          if (!(event.target instanceof Element)) return;
          const inside = (el) =>
            el &&
            (this.contains(el) || this.sheets().some((s) => s.contains(el)));

          const opener = event.target.closest("[data-tb-open]");
          if (inside(opener)) {
            event.preventDefault();
            // From inside another sheet, focus returns to what opened that sheet: the
            // opener itself is about to be hidden.
            const parent = opener.closest("pdp-sheet");
            const returnTo = (parent && parent.returnFocusTo) || opener;
            this.sheets().forEach((sheet) => setOpen(sheet, false));
            setOpen(this.sheet(opener.dataset.tbOpen), true, returnTo);
            return;
          }
          const start = event.target.closest("[data-tb-tbyb-start]");
          if (inside(start)) {
            this.startTryBeforeYouBuy(event, start);
            return;
          }
          // Need help? opens Assistance (assistance.js); the sheet underneath closes without
          // pulling focus back from the chat.
          const help = event.target.closest("[data-assistance-trigger]");
          if (inside(help)) {
            this.sheets().forEach((sheet) => {
              sheet.returnFocusTo = null;
              setOpen(sheet, false);
            });
          }
        };
        document.addEventListener("click", this.onDocumentClick);
      }

      // TRY BEFORE YOU BUY: the size picker adds the piece, the picker's add opens the cart
      // drawer, then the drawer switches to its Try Before You Buy tab. setMode refuses while a
      // cart write is in flight, and the drawer re-renders on the add, so the mode is applied
      // after the add has settled and retried until the re-rendered cart reports it.
      startTryBeforeYouBuy(event, start) {
        const productInfo = this.closest("product-info") || document;
        const picker = productInfo.querySelector("pdp-size-picker");
        event.preventDefault();
        this.sheets().forEach((sheet) => setOpen(sheet, false));
        if (picker && typeof picker.open === "function") {
          this.awaitTryBeforeYouBuyAdd(picker);
          picker.open({ intent: "tbyb" });
          return;
        }
        // No picker on the page: open the drawer already on that tab, as before.
        const drawer = document.querySelector("cart-drawer");
        if (!drawer || typeof drawer.open !== "function") return;
        const cart = drawer.querySelector("tbyb-cart");
        if (cart && typeof cart.setMode === "function") cart.setMode("tbyb");
        drawer.open(start);
      }

      // The intent belongs to one picker session: only the add the picker makes for it
      // (published with intent "tbyb") switches the tab. When the picker closes without that
      // add, the intent is dropped after a short grace, so a later ordinary ADD TO CART stays a
      // plain purchase.
      awaitTryBeforeYouBuyAdd(picker) {
        if (this.stopTryBeforeYouBuyIntent) this.stopTryBeforeYouBuyIntent();
        if (typeof subscribe !== "function") return;
        let grace = null;
        const onClose = () => {
          clearTimeout(grace);
          grace = setTimeout(stop, PICKER_CLOSE_GRACE);
        };
        const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.intent !== "tbyb") return;
          stop();
          this.applyTryBeforeYouBuyMode();
        });
        const stop = () => {
          clearTimeout(grace);
          unsubscribe();
          PICKER_CLOSE_EVENTS.forEach((name) =>
            picker.removeEventListener(name, onClose),
          );
          if (this.stopTryBeforeYouBuyIntent === stop)
            this.stopTryBeforeYouBuyIntent = null;
        };
        PICKER_CLOSE_EVENTS.forEach((name) =>
          picker.addEventListener(name, onClose),
        );
        this.stopTryBeforeYouBuyIntent = stop;
      }

      applyTryBeforeYouBuyMode() {
        let tries = 0;
        const attempt = () => {
          const cart = document.querySelector("cart-drawer tbyb-cart");
          if (cart && typeof cart.setMode === "function") {
            cart.setMode("tbyb");
            if (cart.mode === "tbyb") return;
          }
          tries += 1;
          if (tries < 20) setTimeout(attempt, 100);
        };
        setTimeout(attempt, 0);
      }

      // The widget re-renders per variant (product-info.js calls its update()). Follow its
      // render so the pickup lines stay right after a size or colour change.
      observePickup() {
        const widget = this.querySelector("pickup-availability");
        if (!widget || typeof MutationObserver === "undefined") return;
        this.pickupObserver = new MutationObserver(() => this.syncPickup());
        this.pickupObserver.observe(widget, { childList: true, subtree: true });
      }

      syncPickup() {
        const widget = this.querySelector("pickup-availability");
        if (!widget) return;
        // The preview renders this button only when a location can be shown; without it (no
        // pickup, or the fetch failed) the pickup half is absent.
        const available = Boolean(
          widget.querySelector("#ShowPickupAvailabilityDrawer"),
        );
        this.pickupAvailable = available;
        this.locations = available
          ? locationsFromDrawer(
              document.querySelector("pickup-availability-drawer"),
              this.knownLocations,
            )
          : [];
        this.renderLocations();
      }

      renderDate() {
        try {
          const int = (key, fallback) => {
            const n = parseInt(this.dataset[key], 10);
            return Number.isFinite(n) ? n : fallback;
          };
          const range = computeDeliveryRange(nowInTimeZone("Asia/Bangkok"), {
            processing: int("processing", 0),
            cutoff: int("cutoff", 14),
            transitMin: int("transitMin", 1),
            transitMax: int("transitMax", 2),
            shipWeekends: this.dataset.weekends !== "false",
          });
          const format = (style) =>
            formatDateRange(range.earliest, range.latest, {
              locale: this.dataset.locale || "en",
              range: this.dataset.range !== "false",
              style,
            });
          const prefix = this.dataset.prefix ? `${this.dataset.prefix} ` : "";
          this.targets("[data-tb-date]").forEach((el) => {
            el.textContent = prefix + format("short");
          });
          const sheetTemplate = this.dataset.sheetDate || "{date}";
          this.targets("[data-tb-sheet-date]").forEach((el) => {
            el.textContent = sheetTemplate.replace("{date}", format("long"));
          });
        } catch (e) {
          /* keep the Liquid fallback on any failure */
        }
      }

      // Phuket express uses its own cut-off on the Bangkok clock; the row and the Delivery
      // sheet say the same word.
      renderPhuket() {
        try {
          const cutoff = parseInt(this.dataset.phuketCutoff, 10);
          const now = nowInTimeZone("Asia/Bangkok");
          const pick = (today, tomorrow) =>
            phuketLabel(
              now.hour,
              Number.isFinite(cutoff) ? cutoff : 18,
              today,
              tomorrow,
            );
          const row = pick(
            this.dataset.phuketToday,
            this.dataset.phuketTomorrow,
          );
          const sheet = pick(
            this.dataset.expressToday,
            this.dataset.expressTomorrow,
          );
          if (row != null)
            this.targets("[data-tb-phuket]").forEach((el) => {
              el.textContent = row;
            });
          if (sheet != null)
            this.targets("[data-tb-express]").forEach((el) => {
              el.textContent = sheet;
            });
        } catch (e) {
          /* keep the Liquid fallback on any failure */
        }
      }

      // Fetch the live cart total, then render how much more is needed for free shipping.
      refreshFreeShipping() {
        const base =
          typeof window !== "undefined" &&
          window.routes &&
          window.routes.cart_url
            ? window.routes.cart_url
            : "/cart";
        fetch(`${base}.js`)
          .then((r) => r.json())
          .then((cart) => this.renderFreeShipping(cart))
          .catch(() => {});
      }

      renderFreeShipping(cart) {
        const heading = this.querySelector("[data-tb-free]");
        if (!heading) return;
        const threshold = parseInt(this.dataset.threshold, 10);
        if (!Number.isFinite(threshold)) return;
        const announce = this.querySelector("[data-tb-free-sr]");
        const met =
          Boolean(cart) &&
          cart.item_count > 0 &&
          threshold - cart.total_price <= 0;
        // Announce only the moment the cart crosses the threshold, never on the first render.
        if (announce)
          announce.textContent =
            met && this.freeMet === false ? this.dataset.freeMetSr || "" : "";
        this.freeMet = met;

        // Empty cart: show the calm threshold line, not an "add ฿X more" nudge.
        if (!cart || cart.item_count === 0) {
          heading.textContent = this.dataset.freeEmpty;
        } else if (met) {
          heading.textContent = this.dataset.freeMet;
        } else {
          const amount = formatMoney(
            threshold - cart.total_price,
            this.dataset.moneyFormat,
          );
          heading.textContent = (this.dataset.freeGap || "").replace(
            "{amount}",
            amount,
          );
        }
      }

      updateVariantState(variantId) {
        if (!this.finalSaleIds) return;
        const id = String(variantId);
        // Final-sale variants are not eligible for returns: the row and the sheet swap to the
        // final-sale copy.
        const isFinalSale = this.finalSaleIds.has(id);
        this.targets('[data-tb-returns="standard"]').forEach((el) => {
          el.hidden = isFinalSale;
        });
        this.targets('[data-tb-returns="final"]').forEach((el) => {
          el.hidden = !isFinalSale;
        });
        this.tbybEligible = this.tbybIds.has(id);
        // The previous variant's locations go until the widget's fetch for this one lands
        // (syncPickup refills them); while it runs or if it fails, the pickup half is absent.
        this.locations = [];
        this.renderLocations();
      }

      renderLocations() {
        const templates = {
          one: this.dataset.readyOne,
          many: this.dataset.readyMany,
        };
        const newWindow = this.dataset.newWindow || "";
        this.targets("[data-tb-locations]").forEach((list) => {
          list.replaceChildren(
            ...this.locations.map((location) => {
              const item = document.createElement("li");
              item.className = "rows-sheet__location";
              const icon = this.querySelector("[data-tb-pin]");
              if (icon) item.append(icon.content.cloneNode(true));
              const text = document.createElement("div");
              const link = document.createElement("a");
              link.className = "rows-sheet__location-name link";
              link.href = mapsUrl(
                location.query || location.address || location.name,
              );
              link.target = "_blank";
              link.rel = "noopener";
              link.textContent = location.name;
              if (newWindow) {
                const hint = document.createElement("span");
                hint.className = "visually-hidden";
                hint.textContent = ` (${newWindow})`;
                link.append(hint);
              }
              const meta = document.createElement("p");
              meta.className = "rows-sheet__meta";
              meta.textContent = [
                readyLine(location.ready, templates),
                location.address,
              ]
                .filter(Boolean)
                .join(" · ");
              text.append(link, meta);
              item.append(text);
              return item;
            }),
          );
        });
        this.targets("[data-tb-pickup-block]").forEach((block) => {
          block.hidden = this.locations.length === 0;
        });
        this.renderTbybRow();
      }

      // One row for Try Before You Buy and pickup, following register §G:
      // eligible + pickup / eligible only / pickup only / neither (hidden).
      renderTbybRow() {
        const row = this.querySelector("[data-tb-tbyb]");
        if (!row) return;
        const eligible = Boolean(this.tbybEligible);
        const count = this.locations.length;
        const pickup = count > 0;
        row.hidden = !eligible && !pickup;
        const setText = (selector, one, many) => {
          const el = row.querySelector(selector);
          if (!el) return;
          el.textContent = (count === 1 ? one : many || "").replace(
            "{n}",
            count,
          );
        };
        setText(
          '[data-tb-tbyb-line="pickup"]',
          this.dataset.tbybPickupOne,
          this.dataset.tbybPickupMany,
        );
        setText(
          '[data-tb-tbyb-line="pickup-only"]',
          this.dataset.pickupOne,
          this.dataset.pickupMany,
        );
        const show = (selector, visible) =>
          row.querySelectorAll(selector).forEach((el) => {
            el.hidden = !visible;
          });
        show('[data-tb-tbyb-title="tbyb"]', eligible);
        show('[data-tb-tbyb-title="pickup"]', !eligible);
        show('[data-tb-tbyb-line="pickup"]', eligible && pickup);
        show('[data-tb-tbyb-line="no-pickup"]', eligible && !pickup);
        show('[data-tb-tbyb-line="pickup-only"]', !eligible && pickup);
        const button = row.querySelector("[data-tb-open]");
        if (button) {
          const target = eligible ? "tbyb" : "pickup";
          button.dataset.tbOpen = target;
          button.setAttribute(
            "aria-controls",
            `PdpSheet-${target}-${this.dataset.sectionId}`,
          );
        }
        this.targets("[data-tb-sheet-tbyb]").forEach((el) => {
          el.hidden = !eligible;
        });
      }
    }
    customElements.define("pdp-trust-badges", PdpTrustBadges);
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      computeLaneRange,
      formatDayRange,
      isShippingDay,
      nextShippingDay,
      addShippingDays,
      addCalendarDays,
      computeDeliveryRange,
      phuketLabel,
      formatDateRange,
      nowInTimeZone,
      formatMoney,
      readyLine,
      mapsUrl,
      addressFromLines,
    };
  }
})(typeof window !== "undefined" ? window : globalThis);
